import React, { useCallback } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { fabric } from 'fabric';
import type { Canvas } from 'fabric/fabric-impl';
import { Icon, IconNames, ToastVariants, CUIColors } from '@dotcom/crown-ui';
import {
  DELETE_ICON_TOP_OFFSET,
  DELETE_ICON_TOP_OFFSET_LANDSCAPE,
  DELETE_ICON_TOP_OFFSET_PORTRAIT,
  TEXT_BOX_WIDTH_OFFSET_LANDSCAPE,
  TEXT_BOX_WIDTH_OFFSET_PORTRAIT,
} from '../../constants/user-zone-textbox-offsets';
import { useAnalyticsContext } from '../../context/analytics-context';
import { useAppContext } from '../../context/app-context/app-context';
import { setIsImageUploadDrawerOpen, setIsToasterOpen } from '../../context/app-context/app-context-actions';
import { useCardContext } from '../../context/card-context';
import { useCropContext } from '../../context/crop-context';
import { useInitializationDataContext } from '../../context/data-context';
import { usePhotoTextZoneControls } from '../../features/card-editor/hooks/use-photo-text-zone-controls';
import { useTextArea } from '../../features/card-editor/hooks/use-text-area';
import { useActiveCanvas, useCardFaceIsEditable, useFeatureFlags, useIsPodProductCode } from '../../hooks';
import { ActionType, useDatadog } from '../../hooks/use-datadog';
import { useIsOneToMany } from '../../hooks/use-is-one-to-many';
import { config } from '../../regional-config';
import colorsList from '../../styles/util.scss';
import { CanvasDataTypes, helperSettingsConfig } from '../../utils';
import { addUserTextHandlers, getObjectsByType, hasAvailablePhotoZones, photoZonesAreFilled } from '../../utils/canvas';
import { getCardFaceClipPath } from '../../utils/canvas-utils/get-card-face-clip-path';
import { createClipPathRect } from '../../utils/canvas-utils/create-clip-path-rect';
import { convertPointToPixel } from '../../utils/fonts';
import { CardType } from '../../global-types/card';
import { isSplitPanelFace, getPanelWidth } from '../../utils/panel-zoom';
import { parseTextAction } from '../../utils/parse-text-action';
import { getFontidByUrlName } from '../../utils/utility/get-fontid-by-url-name';
import { getUserZoneTextboxOffsets } from '../../utils/utility/get-user-zone-textbox-offsets';
import { getTextSettings, removeEditableAreaButtons } from '../card-editor/utils';
import { ControlButton } from '../control-button/control-button';
import { useShowToolbar } from './hooks/use-show-toolbar';
import { ToolbarProps } from './toolbar-types';
import styles from './toolbar.module.scss';

export const Toolbar = ({ addClass, onToolbarItemClick, checkOnCanvas = true }: ToolbarProps) => {
  const { appDispatch } = useAppContext();
  const { cardState } = useCardContext();
  const { updateEditFormats, firstElementSelected, trackPhotoUploadMethod } = useAnalyticsContext();
  const { initializedDataState } = useInitializationDataContext();
  const { isUK } = initializedDataState;
  const { isCropping } = useCropContext();
  const { scalingFactor } = helperSettingsConfig;
  const projectTypeCode = initializedDataState.data?.project_type_code;
  const { t } = useTranslation();
  const { IS_TEXTBOX_OVERLAY_ENABLED, IS_POD_USER_TEXTBOX_OVERLAY_ENABLED } = useFeatureFlags();
  const canvas = useActiveCanvas();
  const isPodProductCode = useIsPodProductCode();
  const { canAddText, type: faceType, isEditable, canAddPhoto } = useCardFaceIsEditable();
  const { showToolbar } = useShowToolbar();
  const isOneToMany = useIsOneToMany();
  const { addCustomTextArea } = useTextArea();
  const { handleTypeAMessage, handleAddHandwriting } = usePhotoTextZoneControls();
  const classes = classNames(styles.toolbar, addClass);
  const { features } = config;
  const currentCardFace = cardState.cardFacesList[cardState.activeCardIndex];
  const shouldShowPodWam =
    (currentCardFace?.type !== 'front' && features?.isPodWamEnabledOnlyInside) || features?.isPodWamEnabled;

  const orientation = cardState.cardFormat;
  const isPortrait = orientation === 'portrait';
  const isLandscape = orientation === 'landscape';

  const { textAction } = useDatadog();

  const openNonEditableToast = useCallback(
    () =>
      setIsToasterOpen(appDispatch, {
        title: '',
        children: faceType ? t('nonEditableMessage.message', { face: faceType }) : t('nonEditableMessage.fallback'),
        variant: ToastVariants.Error,
      }),
    [t, faceType],
  );
  const openCoverIsFullToast = useCallback(
    () =>
      setIsToasterOpen(appDispatch, {
        title: '',
        children: t('nonEditableMessage.coverIsFull'),
        variant: ToastVariants.Error,
      }),
    [],
  );

  const handleTextClick = () => {
    if (!canAddText) {
      openNonEditableToast();
      return;
    }

    // Move to hook
    if (canvas && canvas.current) {
      if (onToolbarItemClick) {
        onToolbarItemClick('text');
      }

      if (IS_TEXTBOX_OVERLAY_ENABLED && IS_POD_USER_TEXTBOX_OVERLAY_ENABLED) {
        // Gets user text box type from current canvas and returns the last one
        // Getting text settings for default text from template
        const defaultTextAreaSettings = { ...initializedDataState.data?.variables.template_data.DefaultTextArea };
        const INITIAL_TEXT_MESSAGE = defaultTextAreaSettings?.Text || `${t('cardEditor.userTextDefaultMessage')}`;
        // Getting current canvas
        const currentCanvas = canvas.current as fabric.Canvas;
        const lastTextBox = getObjectsByType(currentCanvas, CanvasDataTypes.UserText).at(-1) as fabric.Textbox;
        // If there's already a textbox on the canvas that is unedited, we want to select it instead of creatinga new one.
        if (lastTextBox?.text === INITIAL_TEXT_MESSAGE) {
          canvas.current.setActiveObject(lastTextBox);
          canvas.current.renderAll();
          textAction(ActionType.ADD_TEXT, parseTextAction(lastTextBox));
          return;
        }

        addCustomTextArea({
          canvas,
          cardFace: currentCardFace,
          onError: () => {
            throw new Error('Error adding custom text area');
          },
        });
      } else {
        const defaultTextAreaSettings = { ...initializedDataState.data?.variables.template_data.DefaultTextArea };
        const INITIAL_TEXT_MESSAGE = defaultTextAreaSettings?.Text || `${t('cardEditor.userTextDefaultMessage')}`;
        const textBoxes = canvas.current as fabric.Canvas;
        const cardFaceClipPath = getCardFaceClipPath(cardState.cardFacesList[cardState.activeCardIndex], 0);
        const lastTextBox = getObjectsByType(textBoxes, CanvasDataTypes.UserText).at(-1) as fabric.Textbox;
        const canvasWidth = canvas.current.getWidth();
        const canvasZoom = canvas.current.getZoom();
        const activeCardFace = cardState.cardFacesList[`${cardState.activeCardIndex}`] ?? {};
        const cardFaceType = activeCardFace.type?.split('-')[0];
        if (onToolbarItemClick) {
          onToolbarItemClick('text');
        }
        if (lastTextBox?.text === INITIAL_TEXT_MESSAGE) {
          canvas.current.setActiveObject(lastTextBox);
          canvas.current.renderAll();
          textAction(ActionType.ADD_TEXT, parseTextAction(lastTextBox));
          return;
        }
        const defaultFallbackColor = isUK ? colorsList[CUIColors.CharcoalGrey] : colorsList[CUIColors.Black];
        const defaultColor = defaultTextAreaSettings?.TextColor || defaultFallbackColor;
        const defaultFontSize = convertPointToPixel(
          initializedDataState.data?.variables.template_data.DefaultTextArea.FontSize || 50,
        );
        defaultTextAreaSettings.FontFamilyId = getFontidByUrlName(
          defaultTextAreaSettings?.FontFamily || '',
          initializedDataState.data?.font_collection.fonts,
        );
        const defaultTextSettings = getTextSettings({
          cardState,
          color: defaultColor,
          defaultFontSize,
          isUK,
          defaultOptions: defaultTextAreaSettings,
          name: CanvasDataTypes.UserText,
        });

        const isPanelZoom =
          isPortrait &&
          (projectTypeCode === CardType.POD || projectTypeCode === CardType.DG) &&
          isSplitPanelFace(activeCardFace.type);
        const isRightPanel = isPanelZoom && activeCardFace.type === 'inside-right';

        const landscapeLeftMargin = 65;
        const portraitLeftMargin = 52;
        const rightPanelLeftOffset = canvasWidth / canvasZoom + portraitLeftMargin;
        const leftMargin = isRightPanel ? rightPanelLeftOffset : portraitLeftMargin;
        defaultTextSettings.left = isLandscape ? landscapeLeftMargin : leftMargin;

        // TODO: all this options should be a defaultTextOptions config file
        const horizontalPadding = 20;
        const totalPadding = 2 * (horizontalPadding * scalingFactor * 2);
        const panelWidth = getPanelWidth({ isPanelZoom, canvasWidth, canvasZoom });
        const width =
          isLandscape || cardFaceType === 'front' || cardFaceType === 'back'
            ? canvasWidth / canvasZoom - totalPadding
            : panelWidth - totalPadding;

        const defaultTextboxHeight = 100;
        const textElement = new fabric.Textbox(INITIAL_TEXT_MESSAGE, {
          top: isPortrait ? DELETE_ICON_TOP_OFFSET_PORTRAIT : DELETE_ICON_TOP_OFFSET_LANDSCAPE,
          width: isPortrait ? width - TEXT_BOX_WIDTH_OFFSET_PORTRAIT : width - TEXT_BOX_WIDTH_OFFSET_LANDSCAPE,
          height: defaultTextboxHeight,
          hasControls: true,
          borderColor: isUK ? '#333333' : '#604099',
          ...defaultTextSettings,
        });
        textElement.set({ data: { type: CanvasDataTypes.UserText } });
        if (cardFaceClipPath) {
          const { TOP_OFFSET, LEFT_OFFSET, RIGHT_OFFSET } = getUserZoneTextboxOffsets(cardState.cardFormat);
          textElement.set({
            top: cardFaceClipPath.top + DELETE_ICON_TOP_OFFSET + TOP_OFFSET,
            left: cardFaceClipPath.left + LEFT_OFFSET,
            width: cardFaceClipPath.width - (LEFT_OFFSET + RIGHT_OFFSET),
          });
          textElement.clipPath = createClipPathRect(cardFaceClipPath);
        }

        addUserTextHandlers(textElement, INITIAL_TEXT_MESSAGE, defaultColor, canvas.current);
        removeEditableAreaButtons(canvas.current);
        canvas.current.add(textElement);
        canvas.current.setActiveObject(textElement);
      }
    }

    updateEditFormats({ eventAction: !firstElementSelected ? 'start' : 'add' });
  };

  const handleImageClick = () => {
    trackPhotoUploadMethod('photo object');
    const currentCanvas = canvas?.current as Canvas;
    // We need to check this first because some cards have photo-zones that can be filled
    //even when the isEditable and canAddPhoto flags are set to false
    if (faceType === 'front' && checkOnCanvas && hasAvailablePhotoZones(currentCanvas)) {
      return openPhotoUploadDrawer();
    }

    if (faceType === 'front' && photoZonesAreFilled(currentCanvas)) {
      return openCoverIsFullToast();
    }

    if (!isEditable || !canAddPhoto) {
      openNonEditableToast();
      return;
    }

    openPhotoUploadDrawer();
  };

  const openPhotoUploadDrawer = () => {
    if (onToolbarItemClick) {
      onToolbarItemClick('photo');
    }
    setIsImageUploadDrawerOpen(appDispatch);
  };

  const handleWriteAMessageClick = () => {
    if ((!canAddPhoto || !canAddText) && isPodProductCode) {
      openNonEditableToast();
      return;
    }

    if (canvas?.current) {
      handleAddHandwriting({
        canvas: canvas.current,
      });
    }
  };

  const handleTypeAMessageClick = () => {
    if (canvas?.current) {
      handleTypeAMessage({
        canvas: canvas.current,
      });
    }
  };

  const buttonProps = {
    P: [
      {
        iconName: IconNames.PhotoslotBold,
        iconLabel: `${t('toolbar.photo')}`,
        testId: 'add-photo-button',
        onClick: handleImageClick,
      },
      {
        iconName: IconNames.TextboxBold,
        iconLabel: `${t('toolbar.text')}`,
        testId: 'add-text-button',
        onClick: handleTextClick,
      },
    ],
    D: [
      {
        iconName: IconNames.PhotoslotBold,
        iconLabel: `${t('toolbar.photo')}`,
        testId: 'add-photo-button',
        onClick: handleImageClick,
      },
      {
        iconName: IconNames.TextboxBold,
        iconLabel: `${t('toolbar.text')}`,
        testId: 'add-text-button',
        onClick: handleTextClick,
      },
    ],
    S: [
      {
        iconName: IconNames.HallmarkSignSendBold,
        iconLabel: `${t('toolbar.writeAMessage')}`,
        testId: 'write-a-message-button',
        onClick: handleWriteAMessageClick,
      },
      {
        iconName: IconNames.HallmarkTypeAMessage,
        iconLabel: `${t('toolbar.typeAMessage')}`,
        testId: 'type-a-message-button',
        onClick: handleTypeAMessageClick,
      },
    ],
  };

  // POD WAM button enabled/disabled based on regional config
  // POD WAM disabled for 1:many cards
  if (shouldShowPodWam && !isOneToMany) {
    buttonProps['P'].push({
      iconName: IconNames.HallmarkSignSendBold,
      iconLabel: `${t('toolbar.writeAMessage')}`,
      testId: 'write-a-message-button',
      onClick: handleWriteAMessageClick,
    });
  }

  return (
    <div id="Toolbar" data-testid="toolbar" className={classes}>
      <ul className={styles.list}>
        {showToolbar &&
          projectTypeCode &&
          !isCropping &&
          buttonProps[`${projectTypeCode}`].map((item, index) => (
            <li key={`list-item-${index}`}>
              <ControlButton
                label={item.iconLabel}
                onClick={item.onClick}
                data-testid={item.testId}
                testId={item.testId}
                area-tabindex={0}
                buttonClass={styles['toolbar-button']}
                textClass={styles['label']}
              >
                <Icon
                  name={item.iconName}
                  size={!isMobile ? '40' : '24'}
                  className={styles.tab}
                  color={'white'}
                  testId={'textbox-bold'}
                />
              </ControlButton>
            </li>
          ))}
      </ul>
    </div>
  );
};
